import React, { useEffect, useMemo, useState } from 'react'
import { updateOrganizationBranding, uploadOrganizationBrandingAsset } from '../../../services/access'
import type { Organization, OrganizationBranding, OrganizationBrandingPayload } from '../../../types/access'
import {
  brandColors,
  brandingBanner,
  brandingField,
  brandingLogo,
  brandingName,
  brandingOrgId,
  formatOrgType,
} from './adminPanelUtils'
import { ErrorBanner, FormModal } from './AdminUiPrimitives'
import { useI18n } from '../../../i18n/I18nContext'
import { normalizeBrandingUrlInput } from '../../../utils/branding'

type Translator = (text: string, vars?: Record<string, string | number>) => string

interface BrandingPanelProps {
  organizations: Organization[]
  brandingRows: OrganizationBranding[]
  selectedOrgId: string
  canManageBranding: boolean
  onSelectOrg: (orgId: string) => void
  onRefresh: () => Promise<void>
}

type UploadedBrandAsset = {
  fileName: string
  mimeType: string
  sizeBytes: number
  uploadedAt?: string
  fileId?: string | null
}

type BrandingMetadata = Record<string, unknown> & {
  logoUpload?: UploadedBrandAsset | null
  bannerUpload?: UploadedBrandAsset | null
}

type AssetKind = 'logo' | 'banner'
type ColorField = 'primaryColor' | 'secondaryColor' | 'accentColor' | 'backgroundColor' | 'surfaceColor' | 'textColor' | 'buttonColor' | 'buttonTextColor'

const MAX_BRANDING_IMAGE_BYTES = 2 * 1024 * 1024
const ACCEPTED_BRANDING_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']
const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

const BRAND_COLOR_PRESETS = ['#2563eb', '#0f172a', '#f59e0b', '#16a34a', '#dc2626', '#7c3aed', '#0891b2', '#111827']

const metadataFromBranding = (branding?: OrganizationBranding): BrandingMetadata => (
  ((branding?.metadata || {}) as BrandingMetadata)
)

const uploadedAssetFromMetadata = (metadata: BrandingMetadata | null | undefined, kind: AssetKind): UploadedBrandAsset | null => {
  const value = metadata?.[`${kind}Upload`]
  if (!value || typeof value !== 'object') return null
  const asset = value as Partial<UploadedBrandAsset>
  if (!asset.mimeType || !asset.fileName || !asset.sizeBytes) return null
  return asset as UploadedBrandAsset
}

const assetPreviewUrl = (branding: OrganizationBranding | undefined, kind: AssetKind, form?: OrganizationBrandingPayload) => {
  // When editing, the form is the source of truth: a manual external URL wins,
  // otherwise an uploaded asset (tracked by file id) renders via its internal
  // route, and a cleared asset (no url, no file id) shows nothing.
  if (form) {
    const external = (kind === 'logo' ? form.logoUrl : form.bannerUrl)?.trim()
    if (external) return external
    const fileId = kind === 'logo' ? form.logoFileId : form.bannerFileId
    const orgId = branding ? brandingOrgId(branding) : ''
    return fileId && orgId ? `/api/orgs/${orgId}/branding/${kind}/file` : ''
  }
  if (kind === 'logo') return brandingLogo(branding)
  return brandingBanner(branding)
}

/** True when the editing form currently references an uploaded (file-id) asset with no manual URL override. */
const isUploadedAssetActive = (kind: AssetKind, form: OrganizationBrandingPayload) => {
  const external = (kind === 'logo' ? form.logoUrl : form.bannerUrl)?.trim()
  const fileId = kind === 'logo' ? form.logoFileId : form.bannerFileId
  return Boolean(fileId) && !external
}

const formFromBranding = (branding?: OrganizationBranding, org?: Organization): OrganizationBrandingPayload => ({
  displayName: brandingName(branding, org?.name),
  shortName: brandingField(branding, 'shortName', 'short_name'),
  legalName: brandingField(branding, 'legalName', 'legal_name'),
  // The editable URL field holds ONLY a manual external URL. Uploaded assets are
  // shown as a preview instead of dumping their internal `/api/…/file` route
  // into the input — otherwise a color-only save would resubmit that route.
  logoUrl: normalizeBrandingUrlInput(brandingField(branding, 'logoUrl', 'logo_url')) || '',
  logoFileId: brandingField(branding, 'logoFileId', 'logo_file_id') || null,
  bannerUrl: normalizeBrandingUrlInput(brandingField(branding, 'bannerUrl', 'banner_url')) || '',
  bannerFileId: brandingField(branding, 'bannerFileId', 'banner_file_id') || null,
  primaryColor: brandingField(branding, 'primaryColor', 'primary_color'),
  secondaryColor: brandingField(branding, 'secondaryColor', 'secondary_color'),
  accentColor: brandingField(branding, 'accentColor', 'accent_color'),
  backgroundColor: brandingField(branding, 'backgroundColor', 'background_color'),
  surfaceColor: brandingField(branding, 'surfaceColor', 'surface_color'),
  textColor: brandingField(branding, 'textColor', 'text_color'),
  buttonColor: brandingField(branding, 'buttonColor', 'button_color'),
  buttonTextColor: brandingField(branding, 'buttonTextColor', 'button_text_color'),
  supportEmail: brandingField(branding, 'supportEmail', 'support_email'),
  supportPhone: brandingField(branding, 'supportPhone', 'support_phone'),
  websiteUrl: brandingField(branding, 'websiteUrl', 'website_url'),
  metadata: metadataFromBranding(branding),
})

const normalizeColorValue = (value?: string | null) => {
  const trimmed = value?.trim() || ''
  return trimmed ? trimmed : null
}

const normalizeBrandingPayload = (form: OrganizationBrandingPayload): OrganizationBrandingPayload => ({
  displayName: form.displayName?.trim() || null,
  shortName: form.shortName?.trim() || null,
  legalName: form.legalName?.trim() || null,
  // Only real external URLs are submitted as overrides; internal asset routes
  // collapse to null so a color-only save never trips URL validation. The
  // file-id fields carry the uploaded asset reference so it is preserved (or
  // explicitly cleared) alongside the save.
  logoUrl: normalizeBrandingUrlInput(form.logoUrl),
  logoFileId: form.logoFileId ?? null,
  bannerUrl: normalizeBrandingUrlInput(form.bannerUrl),
  bannerFileId: form.bannerFileId ?? null,
  primaryColor: normalizeColorValue(form.primaryColor),
  secondaryColor: normalizeColorValue(form.secondaryColor),
  accentColor: normalizeColorValue(form.accentColor),
  backgroundColor: normalizeColorValue(form.backgroundColor),
  surfaceColor: normalizeColorValue(form.surfaceColor),
  textColor: normalizeColorValue(form.textColor),
  buttonColor: normalizeColorValue(form.buttonColor),
  buttonTextColor: normalizeColorValue(form.buttonTextColor),
  supportEmail: form.supportEmail?.trim() || null,
  supportPhone: form.supportPhone?.trim() || null,
  websiteUrl: form.websiteUrl?.trim() || null,
  metadata: form.metadata || undefined,
})

const brandingErrorMessage = (err: any, t: Translator) => {
  const response = err?.response?.data
  const errors = Array.isArray(response?.errors) ? response.errors : []
  if (errors.length > 0) {
    return errors
      .map((error: { field?: string; message?: string }) => (
        error.field && error.field !== 'branding'
          ? `${error.field}: ${error.message || t('Invalid value.')}`
          : error.message || t('Invalid value.')
      ))
      .join(' ')
  }
  return response?.message || t('Unable to update organization branding.')
}

const fileSizeLabel = (bytes: number) => {
  if (!Number.isFinite(bytes)) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 102.4) / 10} KB`
  return `${Math.round(bytes / (1024 * 102.4)) / 10} MB`
}

const isValidColorOrEmpty = (value?: string | null) => {
  const trimmed = value?.trim() || ''
  return !trimmed || HEX_COLOR_PATTERN.test(trimmed)
}

const colorPickerValue = (value?: string | null) => (
  HEX_COLOR_PATTERN.test(value || '') && (value || '').length === 7 ? value || '#2563eb' : '#2563eb'
)

const colorFieldLabels: Record<ColorField, string> = {
  primaryColor: 'Primary color',
  secondaryColor: 'Secondary color',
  accentColor: 'Accent color',
  backgroundColor: 'Background color',
  surfaceColor: 'Surface color',
  textColor: 'Text color',
  buttonColor: 'Button color',
  buttonTextColor: 'Button text color',
}

export const BrandingPanel: React.FC<BrandingPanelProps> = ({
  organizations,
  brandingRows,
  selectedOrgId,
  canManageBranding,
  onSelectOrg,
  onRefresh,
}) => {
  const { t } = useI18n()
  const selectedOrg = organizations.find((org) => org.id === selectedOrgId)
  const selectedBranding = brandingRows.find((row) => brandingOrgId(row) === selectedOrgId)
  const [form, setForm] = useState<OrganizationBrandingPayload>(() => formFromBranding(selectedBranding, selectedOrg))
  const [saving, setSaving] = useState(false)
  const [showEditor, setShowEditor] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const selectedMetadata = useMemo(() => metadataFromBranding(selectedBranding), [selectedBranding])

  useEffect(() => {
    setForm(formFromBranding(selectedBranding, selectedOrg))
  }, [selectedBranding, selectedOrg])

  const selectOrg = (orgId: string) => {
    onSelectOrg(orgId)
    const org = organizations.find((item) => item.id === orgId)
    const branding = brandingRows.find((row) => brandingOrgId(row) === orgId)
    setForm(formFromBranding(branding, org))
    setMessage('')
    setError('')
    setShowEditor(true)
  }

  const updateField = <K extends keyof OrganizationBrandingPayload>(field: K, value: OrganizationBrandingPayload[K]) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const updateMetadata = (nextMetadata: BrandingMetadata) => {
    setForm((current) => ({ ...current, metadata: nextMetadata }))
  }

  const updateColor = (field: ColorField, value: string) => {
    updateField(field, value as OrganizationBrandingPayload[ColorField])
  }

  const clearAsset = (kind: AssetKind) => {
    const metadata = { ...((form.metadata || selectedMetadata || {}) as BrandingMetadata) }
    delete metadata[`${kind}Upload`]
    updateMetadata(metadata)
    if (kind === 'logo') {
      setForm((current) => ({ ...current, logoUrl: null, logoFileId: null, metadata }))
    } else {
      setForm((current) => ({ ...current, bannerUrl: null, bannerFileId: null, metadata }))
    }
  }

  const handleAssetUpload = async (kind: AssetKind, fileList: FileList | null) => {
    const file = fileList?.[0]
    if (!file) return
    setError('')
    if (!ACCEPTED_BRANDING_IMAGE_TYPES.includes(file.type)) {
      setError(t('Branding images must be PNG, JPEG, WebP, or SVG.'))
      return
    }
    if (file.size > MAX_BRANDING_IMAGE_BYTES) {
      setError(t('Branding images must be 2 MB or smaller.'))
      return
    }
    if (!selectedOrgId) return
    setSaving(true)
    setMessage('')
    try {
      const branding = await uploadOrganizationBrandingAsset(selectedOrgId, kind, file)
      setForm(formFromBranding(branding, selectedOrg))
      await onRefresh()
      setMessage(t('{asset} uploaded.', { asset: kind === 'logo' ? t('Logo') : t('Banner') }))
    } catch (err: any) {
      setError(brandingErrorMessage(err, t))
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canManageBranding || !selectedOrgId) return
    const invalidColor = (['primaryColor', 'secondaryColor', 'accentColor', 'backgroundColor', 'surfaceColor', 'textColor', 'buttonColor', 'buttonTextColor'] as ColorField[])
      .find((field) => !isValidColorOrEmpty(form[field]))
    if (invalidColor) {
      setError(t('{label} must be a hex color like #2563eb.', { label: t(colorFieldLabels[invalidColor]) }))
      return
    }
    setSaving(true)
    setMessage('')
    setError('')
    try {
      await updateOrganizationBranding(selectedOrgId, normalizeBrandingPayload(form))
      await onRefresh()
      setMessage(t('Branding updated.'))
      setShowEditor(false)
    } catch (err: any) {
      setError(brandingErrorMessage(err, t))
    } finally {
      setSaving(false)
    }
  }

  const renderAssetField = (kind: AssetKind, label: string) => {
    const urlField = kind === 'logo' ? 'logoUrl' : 'bannerUrl'
    const preview = assetPreviewUrl(selectedBranding, kind, form)
    const uploaded = uploadedAssetFromMetadata((form.metadata || selectedMetadata) as BrandingMetadata, kind)
    const showingUpload = isUploadedAssetActive(kind, form)
    const inputId = `brand-${kind}`
    const fileInputId = `brand-${kind}-upload`
    return (
      <div className="form-row" style={{ gridColumn: kind === 'banner' ? '1 / -1' : undefined }}>
        <label htmlFor={inputId}>{label}</label>
        <div className="stack" style={{ gap: '0.6rem' }}>
          {preview ? (
            <div className="panel stack" style={{ padding: '0.75rem', gap: '0.4rem', background: 'var(--surface-muted)' }}>
              <img
                src={preview}
                alt={t('{label} preview', { label })}
                className={kind === 'logo' ? 'admin-brand-logo' : undefined}
                style={kind === 'banner'
                  ? { width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 12 }
                  : undefined}
              />
              {showingUpload ? <p className="helper" data-testid={`brand-${kind}-uploaded-tag`} style={{ margin: 0 }}>{t('Uploaded asset')}</p> : null}
            </div>
          ) : <p className="helper">{t('Upload an image, or paste an image URL.')}</p>}
          <input
            id={fileInputId}
            type="file"
            className="input"
            accept={ACCEPTED_BRANDING_IMAGE_TYPES.join(',')}
            onChange={(event) => handleAssetUpload(kind, event.target.files)}
            disabled={!canManageBranding}
          />
          {uploaded ? <p className="helper">{t('Selected')}: {uploaded.fileName} · {fileSizeLabel(uploaded.sizeBytes)}</p> : null}
          <input
            id={inputId}
            className="input"
            value={String(form[urlField] || '')}
            onChange={(event) => updateField(urlField, event.target.value)}
            disabled={!canManageBranding}
            placeholder="https://example.com/image.png"
          />
          <p className="helper">{showingUpload
            ? t('Using the uploaded image above. Leave blank to keep it, or paste an external URL to override.')
            : t('URL is optional. Upload is friendlier; URL is there for advanced setups.')}</p>
          {(preview || form[urlField]) ? (
            <button type="button" className="btn btn-ghost" onClick={() => clearAsset(kind)} disabled={!canManageBranding}>{t('Clear {label}', { label: label.toLowerCase() })}</button>
          ) : null}
        </div>
      </div>
    )
  }

  const renderColorField = (field: ColorField) => {
    const value = String(form[field] || '')
    const valid = isValidColorOrEmpty(value)
    return (
      <div className="form-row">
        <label htmlFor={`brand-${field}`}>{t(colorFieldLabels[field])}</label>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <input
            id={`brand-${field}-picker`}
            type="color"
            value={colorPickerValue(value)}
            onChange={(event) => updateColor(field, event.target.value)}
            disabled={!canManageBranding}
            aria-label={t('{label} picker', { label: t(colorFieldLabels[field]) })}
            style={{ width: 48, minWidth: 48, height: 40, padding: 2 }}
          />
          <input
            id={`brand-${field}`}
            className="input"
            value={value}
            onChange={(event) => updateColor(field, event.target.value)}
            disabled={!canManageBranding}
            placeholder="#2563eb"
            aria-invalid={!valid}
          />
          {valid && value ? <span className="admin-color-swatch" style={{ background: value }} title={value} /> : null}
        </div>
        {!valid ? <p className="helper" role="alert">{t('Use #RGB or #RRGGBB format.')}</p> : <p className="helper">{t('Use the picker or enter a hex color.')}</p>}
        <div className="admin-color-hints" aria-label={t('{label} presets', { label: t(colorFieldLabels[field]) })}>
          {BRAND_COLOR_PRESETS.map((color) => (
            <button
              key={`${field}-${color}`}
              type="button"
              className="admin-color-swatch"
              style={{ background: color, border: value.toLowerCase() === color.toLowerCase() ? '2px solid var(--text)' : undefined }}
              title={color}
              aria-label={t('Use {color}', { color })}
              onClick={() => updateColor(field, color)}
              disabled={!canManageBranding}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="stack">
      <div className="admin-summary-grid">
        {organizations.map((org) => {
          const branding = brandingRows.find((row) => brandingOrgId(row) === org.id)
          const logo = assetPreviewUrl(branding, 'logo')
          return (
            <article key={org.id} className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <div className="admin-brand-lockup">
                {logo ? <img src={logo} alt="" className="admin-brand-logo" /> : (
                  <div className="admin-brand-logo admin-brand-logo--fallback">{brandingName(branding, org.name).slice(0, 1).toUpperCase()}</div>
                )}
                <div>
                  <strong>{brandingName(branding, org.name)}</strong>
                  <p className="text-muted" style={{ margin: 0 }}>{t(formatOrgType(org.org_type))}</p>
                </div>
              </div>
              <div className="admin-color-hints">
                {brandColors(branding).map((color) => (
                  <span key={color} className="admin-color-swatch" style={{ background: color }} title={color} />
                ))}
              </div>
              <button type="button" className="btn btn-ghost" onClick={() => selectOrg(org.id)}>{t('Edit branding')}</button>
            </article>
          )
        })}
      </div>

      <FormModal
        title={t('Branding settings')}
        description={t('Configure the selected agency or company brand without expanding settings into the workspace.')}
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        size="lg"
      >
        <form className="stack" onSubmit={handleSubmit}>
          <div className="admin-panel-toolbar">
            <div className="form-row admin-select-row">
              <label htmlFor="branding-org">{t('Organization')}</label>
              <select id="branding-org" className="input" value={selectedOrgId} onChange={(event) => selectOrg(event.target.value)}>
                {organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
              </select>
            </div>
            {!canManageBranding ? <p className="helper">{t('Branding edits require tenant branding, organization, or RBAC management permission.')}</p> : null}
          </div>
          <div className="admin-form-grid">
            <div className="form-row"><label htmlFor="brand-display">{t('Display name')}</label><input id="brand-display" className="input" value={form.displayName || ''} onChange={(event) => updateField('displayName', event.target.value)} disabled={!canManageBranding} /></div>
            <div className="form-row"><label htmlFor="brand-short">{t('Short name')}</label><input id="brand-short" className="input" value={form.shortName || ''} onChange={(event) => updateField('shortName', event.target.value)} disabled={!canManageBranding} /></div>
            <div className="form-row"><label htmlFor="brand-legal">{t('Legal name')}</label><input id="brand-legal" className="input" value={form.legalName || ''} onChange={(event) => updateField('legalName', event.target.value)} disabled={!canManageBranding} /></div>
            {renderAssetField('logo', t('Logo'))}
            {renderAssetField('banner', t('Banner'))}
            {renderColorField('primaryColor')}
            {renderColorField('secondaryColor')}
            {renderColorField('accentColor')}
            {renderColorField('backgroundColor')}
            {renderColorField('surfaceColor')}
            {renderColorField('textColor')}
            {renderColorField('buttonColor')}
            {renderColorField('buttonTextColor')}
            <div className="form-row"><label htmlFor="brand-email">{t('Support email')}</label><input id="brand-email" className="input" value={form.supportEmail || ''} onChange={(event) => updateField('supportEmail', event.target.value)} disabled={!canManageBranding} /></div>
            <div className="form-row"><label htmlFor="brand-phone">{t('Support phone')}</label><input id="brand-phone" className="input" value={form.supportPhone || ''} onChange={(event) => updateField('supportPhone', event.target.value)} disabled={!canManageBranding} /></div>
            <div className="form-row"><label htmlFor="brand-website">{t('Website URL')}</label><input id="brand-website" className="input" value={form.websiteUrl || ''} onChange={(event) => updateField('websiteUrl', event.target.value)} disabled={!canManageBranding} /></div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={!canManageBranding || saving}>{saving ? t('Saving...') : t('Save branding')}</button>
        </form>
      </FormModal>
      {message ? <p className="helper">{message}</p> : null}
      <ErrorBanner message={error} testId="branding-error" />
    </div>
  )
}
